import React, {
  useEffect,
  useState,
} from "react";

import {
  FaBell,
  FaGift,
  FaUserCircle,
  FaTh,
  FaChevronRight,
} from "react-icons/fa";

import {
  collection,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";

import {
  useNavigate,
} from "react-router-dom";

import {
  db,
} from "../firebase";

import "./Header.css";


/* =========================================================
   FIRESTORE BALANCE FIELDS
========================================================= */

const fieldMap = {
  TZS: "tshBalance",
  USDT: "usdtBalance",
  KES: "KESBalance",
  UGX: "UGXBalance",
  RWF: "RWFBalance",
  BIF: "BIFBalance",
  ZMW: "ZMWBalance",
  MWK: "MWKBalance",
  MZN: "MZNBalance",
  USD: "USDBalance",
  SSP: "SSPBalance",
  BWP: "BWPBalance",
  MGA: "MGABalance",
};


const Header = () => {

  const navigate = useNavigate();


  /* =========================================================
     HEADER SPACE CONTROL

     Header ikionekana:
       body.has-header

     Header ikiondoka:
       body.has-header inaondolewa

     Hii inasaidia kuondoa nafasi ya
     header kwenye pages nyingine.
  ========================================================= */

  useEffect(() => {

    document.body.classList.add(
      "has-header"
    );


    return () => {

      document.body.classList.remove(
        "has-header"
      );

    };

  }, []);


  /* =========================================================
     BALANCES
  ========================================================= */

  const [
    balances,
    setBalances,
  ] = useState({});


  /* =========================================================
     BALANCE MENU
  ========================================================= */

  const [
    balanceMenuOpen,
    setBalanceMenuOpen,
  ] = useState(false);


  /* =========================================================
     SELECTED CURRENCY
  ========================================================= */

  const [
    selectedCurrency,
    setSelectedCurrency,
  ] = useState(null);


  /* =========================================================
     UNREAD NOTIFICATIONS
  ========================================================= */

  const [
    unreadNotifications,
    setUnreadNotifications,
  ] = useState(0);


  /* =========================================================
     LOAD BALANCE REAL-TIME
  ========================================================= */

  useEffect(() => {

    const username =
      localStorage.getItem(
        "username"
      );


    if (!username) {

      setBalances({});

      return;

    }


    const userQuery =
      query(
        collection(
          db,
          "users"
        ),

        where(
          "username",
          "==",
          username
        )
      );


    const unsubscribe =
      onSnapshot(
        userQuery,

        (snapshot) => {

          if (
            snapshot.empty
          ) {

            setBalances({});

            return;

          }


          const userData =
            snapshot.docs[0].data();


          const loadedBalances =
            {};


          Object.entries(
            fieldMap
          ).forEach(
            (
              [
                currency,
                field,
              ]
            ) => {

              const value =
                Number(
                  userData[field] ?? 0
                );


              loadedBalances[
                currency
              ] =
                Number.isFinite(
                  value
                )
                  ? value
                  : 0;

            }
          );


          setBalances(
            loadedBalances
          );

        },

        (error) => {

          console.error(
            "Header real-time balance error:",
            error
          );


          setBalances({});

        }
      );


    return () => {

      unsubscribe();

    };

  }, []);


  /* =========================================================
     LOAD UNREAD NOTIFICATIONS REAL-TIME
  ========================================================= */

  useEffect(() => {

    const username =
      localStorage.getItem(
        "username"
      );


    if (!username) {

      setUnreadNotifications(
        0
      );

      return;

    }


    const notificationsQuery =
      query(
        collection(
          db,
          "notifications"
        ),

        where(
          "username",
          "==",
          username
        )
      );


    const unsubscribe =
      onSnapshot(
        notificationsQuery,

        (snapshot) => {

          let unreadCount =
            0;


          snapshot.forEach(
            (
              notificationDoc
            ) => {

              const notificationData =
                notificationDoc.data();


              if (
                notificationData.read !==
                true
              ) {

                unreadCount += 1;

              }

            }
          );


          setUnreadNotifications(
            unreadCount
          );

        },

        (error) => {

          console.error(
            "Unread notifications error:",
            error
          );


          setUnreadNotifications(
            0
          );

        }
      );


    return () => {

      unsubscribe();

    };

  }, []);


  /* =========================================================
     AVAILABLE BALANCES
  ========================================================= */

  const availableBalances =
    Object.entries(
      balances
    )
      .filter(
        ([, value]) =>
          Number(
            value || 0
          ) > 0
      )
      .map(
        (
          [
            currency,
            balance,
          ]
        ) => ({

          currency,

          balance:
            Number(
              balance || 0
            ),

        })
      );


  /* =========================================================
     DEFAULT SELECTED CURRENCY
  ========================================================= */

  useEffect(() => {

    if (
      availableBalances.length ===
      0
    ) {

      setSelectedCurrency(
        null
      );

      return;

    }


    const selectedStillExists =
      availableBalances.some(
        (
          item
        ) =>
          item.currency ===
          selectedCurrency
      );


    if (
      !selectedStillExists
    ) {

      setSelectedCurrency(
        availableBalances[0]
          .currency
      );

    }

  }, [
    balances,
    selectedCurrency,
  ]);


  /* =========================================================
     FORMAT MONEY
  ========================================================= */

  const formatMoney =
    (
      value
    ) => {

      const number =
        Number(
          value ?? 0
        );


      if (
        !Number.isFinite(
          number
        )
      ) {

        return "0.00";

      }


      return number.toLocaleString(
        "en-GB",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      );

    };


  /* =========================================================
     MULTIPLE BALANCES
  ========================================================= */

  const hasMultipleBalances =
    availableBalances.length >
    1;


  /* =========================================================
     CURRENT BALANCE
  ========================================================= */

  const getCurrentBalance =
    () => {

      if (
        availableBalances.length ===
        0
      ) {

        return {

          currency:
            "TZS",

          balance:
            0,

        };

      }


      const selected =
        availableBalances.find(
          (
            item
          ) =>
            item.currency ===
            selectedCurrency
        );


      return (
        selected ||
        availableBalances[0]
      );

    };


  const currentBalance =
    getCurrentBalance();


  /* =========================================================
     GRID
  ========================================================= */

  const handleGridClick =
    () => {

      setBalanceMenuOpen(
        false
      );

      navigate(
        "/all-pages"
      );

    };


  /* =========================================================
     GRID DOT
  ========================================================= */

  const handleGridDotClick =
    (
      event
    ) => {

      event.stopPropagation();

      setBalanceMenuOpen(
        false
      );

      navigate(
        "/all-pages"
      );

    };


  /* =========================================================
     BALANCE CLICK
  ========================================================= */

  const handleBalanceClick =
    () => {

      if (
        hasMultipleBalances
      ) {

        setBalanceMenuOpen(
          (
            previous
          ) =>
            !previous
        );

        return;

      }


      navigate(
        "/all-pages"
      );

    };


  /* =========================================================
     SELECT CURRENCY
  ========================================================= */

  const handleSelectCurrency =
    (
      currency
    ) => {

      setSelectedCurrency(
        currency
      );

      setBalanceMenuOpen(
        false
      );

    };


  /* =========================================================
     GIFT
  ========================================================= */

  const handleGiftClick =
    () => {

      navigate(
        "/gift"
      );

    };


  /* =========================================================
     NOTIFICATIONS
  ========================================================= */

  const handleBellClick =
    () => {

      navigate(
        "/notifications"
      );

    };


  /* =========================================================
     PROFILE
  ========================================================= */

  const handleProfileClick =
    () => {

      navigate(
        "/profile"
      );

    };


  /* =========================================================
     SPORTS
  ========================================================= */

  const handleSportsClick =
    () => {

      navigate(
        "/sports"
      );

    };


  /* =========================================================
     GROWTH PLAN
  ========================================================= */

  const handleCasinoClick =
    () => {

      navigate(
        "/Growth-Plan"
      );

    };


  /* =========================================================
     RETURN
  ========================================================= */

  return (

    <div className="header">

      {/* =====================================================
          HEADER TOP
      ===================================================== */}

      <div className="header-top">

        {/* ===================================================
            GRID
        =================================================== */}

        <div className="header-grid-wrapper">

          <button
            type="button"
            className="header-grid-btn"
            onClick={
              handleGridClick
            }
            aria-label="Assets"
          >

            <FaTh
              className="icon"
            />

          </button>


          <button
            type="button"
            className="header-grid-dot"
            onClick={
              handleGridDotClick
            }
            aria-label="All pages"
          />

        </div>


        {/* ===================================================
            LOGO
        =================================================== */}

        <button
          type="button"
          className="logo"
          onClick={
            handleSportsClick
          }
          aria-label="Sports"
        >

          <span className="logo-text">

            B . F . C

          </span>

        </button>


        {/* ===================================================
            BALANCE
        =================================================== */}

        <div className="header-balance-wrapper">

          <button
            type="button"
            className="header-balance"
            onClick={
              handleBalanceClick
            }
            aria-label="View balances"
          >

            <span className="header-balance-text">

              {currentBalance.currency}{" "}

              {formatMoney(
                currentBalance.balance
              )}

            </span>


            {hasMultipleBalances && (

              <FaChevronRight
                className={
                  balanceMenuOpen
                    ? "header-balance-arrow rotate"
                    : "header-balance-arrow"
                }
              />

            )}

          </button>


          {/* =================================================
              BALANCE MENU
          ================================================= */}

          {balanceMenuOpen &&
            hasMultipleBalances && (

              <div className="header-balance-menu">

                {availableBalances.map(
                  (
                    item
                  ) => {

                    const isSelected =
                      item.currency ===
                      selectedCurrency;


                    return (

                      <button
                        type="button"
                        key={
                          item.currency
                        }
                        className={
                          isSelected
                            ? "header-balance-option active"
                            : "header-balance-option"
                        }
                        onClick={() =>
                          handleSelectCurrency(
                            item.currency
                          )
                        }
                      >

                        <span>

                          {item.currency}

                        </span>


                        <strong>

                          {formatMoney(
                            item.balance
                          )}

                        </strong>

                      </button>

                    );

                  }
                )}

              </div>

            )}

        </div>


        {/* ===================================================
            RIGHT ICONS
        =================================================== */}

        <div className="header-icons">

          {/* =================================================
              GIFT
          ================================================= */}

          <button
            type="button"
            className="header-icon-btn"
            onClick={
              handleGiftClick
            }
            aria-label="Gift"
          >

            <FaGift
              className="icon"
            />

            <span className="header-notification-dot" />

          </button>


          {/* =================================================
              NOTIFICATION
          ================================================= */}

          <button
            type="button"
            className="header-icon-btn header-bell-btn"
            onClick={
              handleBellClick
            }
            aria-label={
              unreadNotifications >
              0
                ? `${unreadNotifications} unread notifications`
                : "Notifications"
            }
          >

            <FaBell
              className="icon"
            />


            {unreadNotifications >
              0 && (

              <span className="header-unread-badge">

                {unreadNotifications >
                99
                  ? "99+"
                  : unreadNotifications}

              </span>

            )}

          </button>


          {/* =================================================
              PROFILE
          ================================================= */}

          <button
            type="button"
            className="header-profile-btn"
            onClick={
              handleProfileClick
            }
            aria-label="Profile"
          >

            <FaUserCircle
              className="icon profile"
            />

          </button>

        </div>

      </div>


      {/* =====================================================
          SPORTS / GROWTH PLAN
      ===================================================== */}

      <div className="header-switch">

        <button
          type="button"
          className="active-btn"
          onClick={
            handleSportsClick
          }
        >

          Market Sport

        </button>


        <button
          type="button"
          onClick={
            handleCasinoClick
          }
        >

          Growth Plan

        </button>

      </div>

    </div>

  );

};


export default Header;